import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  ChangeUserPasswordRequest,
  CreateUserRequest,
  UpdateUserRequest,
  UserDto,
} from './types';

const QK_LIST = ['users'] as const;

export function useUsers() {
  return useQuery({
    queryKey: QK_LIST,
    queryFn: async () => (await apiClient.get<UserDto[]>('/api/users')).data,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateUserRequest) =>
      (await apiClient.post<UserDto>('/api/users', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_LIST }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateUserRequest }) =>
      (await apiClient.put<UserDto>(`/api/users/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_LIST }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/users/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_LIST }),
  });
}

export function useChangeUserPassword() {
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: ChangeUserPasswordRequest }) => {
      await apiClient.post(`/api/users/${id}/change-password`, request);
    },
  });
}
